import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { resolveInitialTheme, THEME_STORAGE_KEY, ThemeContext } from './theme-context';
import type { Theme } from './theme-context';

export interface ThemeProviderProps {
  readonly children: ReactNode;
}

/**
 * Fournit le thème courant (dark par défaut, light via `data-theme="light"`)
 * et le mécanisme de bascule :
 * - état initial résolu par `resolveInitialTheme()` (choix persisté prioritaire,
 *   sinon `prefers-color-scheme` explicite → light, sinon dark) ;
 * - effet : pose/retire `data-theme="light"` sur `document.documentElement`
 *   (dark = aucun attribut, défaut de marque) ;
 * - `toggleTheme` : bascule dark↔light et persiste le choix dans localStorage
 *   (clé `THEME_STORAGE_KEY`). La persistance n'a lieu QUE sur une bascule
 *   explicite : le thème initial dérivé du système n'est jamais écrit, ce qui
 *   préserve la règle « premier chargement sans choix → on honore le système ».
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(resolveInitialTheme);

  // Pose/retire data-theme="light" sur <html> (dark = aucun attribut).
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else {
      root.removeAttribute('data-theme');
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // localStorage indisponible (privé/quota) : persistance silencieuse,
        // le thème reste actif pour la session courante.
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
